import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api';
import '../../styles/PixPayment.css';

const PixPayment = ({ curso, includesResume = true, onPaymentSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [paymentData, setPaymentData] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState('idle');
  const [error, setError] = useState('');
  const [checkingStatus, setCheckingStatus] = useState(false);

  const createPixPayment = async () => {
    try {
      setLoading(true);
      setError('');

      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Token de autenticação não encontrado');
      }

      const paymentRequest = {
        curso_id: curso.id,
        payment_method: 'pix',
        includes_resume: includesResume,
      };

      const response = await axios.post(`${API_BASE_URL}/payment`, paymentRequest, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success) {
        setPaymentData(response.data);
        setPaymentStatus('pending');
      } else {
        throw new Error(response.data.error || 'Erro ao criar pagamento');
      }

    } catch (err) {

      setError(err.response?.data?.error || err.message || 'Erro ao processar pagamento');
    } finally {
      setLoading(false);
    }
  };

  // Polling automático: consulta o MercadoPago diretamente a cada 5s enquanto pendente
  useEffect(() => {
    if (!paymentData?.payment_id || paymentStatus !== 'pending') return;

    setCheckingStatus(true);

    const checkStatus = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.post(
          `${API_BASE_URL}/payments/${paymentData.payment_id}/check-status`,
          {},
          { headers: { 'Authorization': `Bearer ${token}` } }
        );

        const status = response.data.status;
        setPaymentStatus(status);

        if (status === 'completed') {
          onPaymentSuccess?.(response.data);
        } else if (status === 'failed' || status === 'cancelled') {
          setError('Pagamento não foi aprovado');
        }
      } catch (err) {

      }
    };

    const interval = setInterval(checkStatus, 5000);

    // Para após 10 minutos
    const timeout = setTimeout(() => {
      clearInterval(interval);
      setCheckingStatus(false);
    }, 600000);

    // Cleanup: cancela interval se o componente desmontar ou status mudar
    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
      setCheckingStatus(false);
    };
  }, [paymentData?.payment_id, paymentStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  const checkPaymentManually = async () => {
    if (!paymentData?.payment_id) return;

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/payments/${paymentData.payment_id}/check-status`,
        {},
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      const { status, updated } = response.data;
      setPaymentStatus(status);

      if (updated) {
        if (status === 'completed') {
          onPaymentSuccess?.(response.data);
        } else if (status === 'failed' || status === 'cancelled') {
          setError('Pagamento não foi aprovado');
        }
        alert(`Status atualizado: ${status}`);
      } else {
        alert(`Status atual: ${status} (não houve mudança)`);
      }
    } catch (err) {

      setError('Erro ao verificar status do pagamento');
    } finally {
      setLoading(false);
    }
  };

  const copyPixCode = () => {
    if (paymentData?.qr_code) {
      navigator.clipboard.writeText(paymentData.qr_code);
      alert('Código PIX copiado para a área de transferência!');
    }
  };

  const downloadQRCode = () => {
    if (paymentData?.qr_code_base64) {
      const link = document.createElement('a');
      link.href = `data:image/png;base64,${paymentData.qr_code_base64}`;
      link.download = 'qr-code-pix.png';
      link.click();
    }
  };

  return (
    <div className="pix-payment">
      <div className="payment-header">
        <h3>Pagamento via PIX</h3>
        <p>Curso: {curso.title}</p>
        <p className="amount">Valor: R$ {Number(paymentData?.amount || curso.price || 29.90).toFixed(2)}</p>
      </div>

      {error && (
        <div className="error-message">
          <p>{error}</p>
        </div>
      )}

      {!paymentData && !loading && (
        <div className="payment-start">
          <p>Clique no botão abaixo para gerar o código PIX:</p>
          <button 
            onClick={createPixPayment}
            disabled={loading}
            className="btn-generate-pix"
          >
            {loading ? 'Gerando PIX...' : 'Gerar Código PIX'}
          </button>
        </div>
      )}

      {paymentData && (
        <div className="payment-details">
          <div className="status-indicator">
            {paymentStatus === 'pending' && (
              <div className="status pending">
                <span className="status-icon">⏳</span>
                <span>Aguardando pagamento...</span>
              </div>
            )}
            {paymentStatus === 'completed' && (
              <div className="status completed">
                <span className="status-icon">✅</span>
                <span>Pagamento aprovado!</span>
              </div>
            )}
            {(paymentStatus === 'failed' || paymentStatus === 'cancelled') && (
              <div className="status failed">
                <span className="status-icon">❌</span>
                <span>Pagamento não aprovado</span>
              </div>
            )}
          </div>

          {paymentStatus === 'pending' && (
            <>
              <div className="pix-code-section">
                <h4>Copie o código PIX:</h4>
                <div className="pix-code-container">
                  <textarea 
                    value={paymentData.qr_code || ''}
                    readOnly
                    className="pix-code-text"
                    rows="4"
                  />
                  <button onClick={copyPixCode} className="btn-copy">
                    📋 Copiar Código
                  </button>
                </div>
              </div>

              <div className="qr-code-section">
                <h4>Ou escaneie o QR Code:</h4>
                {paymentData.qr_code_base64 && (
                  <div className="qr-code-container">
                    <img 
                      src={`data:image/png;base64,${paymentData.qr_code_base64}`}
                      alt="QR Code PIX"
                      className="qr-code-image"
                    />
                    <button onClick={downloadQRCode} className="btn-download">
                      📥 Baixar QR Code
                    </button>
                  </div>
                )}
              </div>

              <div className="payment-instructions">
                <h4>Instruções:</h4>
                <ol>
                  <li>Abra o app do seu banco</li>
                  <li>Escolha a opção PIX</li>
                  <li>Escaneie o QR Code ou cole o código</li>
                  <li>Confirme o pagamento</li>
                  <li>Aguarde a confirmação automática</li>
                </ol>
              </div>

              {checkingStatus && (
                <div className="checking-status">
                  <div className="spinner"></div>
                  <p>Verificando pagamento automaticamente...</p>
                </div>
              )}

              <div className="manual-check-section">
                <button 
                  onClick={checkPaymentManually}
                  disabled={loading}
                  className="btn-check-status"
                >
                  {loading ? 'Verificando...' : 'Verificar Status Manualmente'}
                </button>
                <p className="manual-check-hint">
                  Use este botão se já fez o pagamento e quer verificar o status imediatamente
                </p>
              </div>

              {paymentData.expires_at && (
                <div className="expiration-warning">
                  <p>⚠️ Este PIX expira em: {new Date(paymentData.expires_at).toLocaleString()}</p>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default PixPayment;
